import React, { useState, useMemo, useCallback } from 'react'
import toast from 'react-hot-toast'
import { ResumeItem } from '../services/apiService'
import { ResumeFitAnalysisResponse, TailoredResumeResponse } from '../services/jobApplicationService'
import { downloadTailoredResumeArtifact } from '../utils/tailoredResumeArtifacts'
import './AssignedResumeViewer.css'

interface AssignedResumeViewerProps {
  jobApplicationId: string
  selectedResumeId: string | null
  resumeSelectionType: 'saved' | 'custom' | null
  availableResumes: ResumeItem[]
  tailoredResume: TailoredResumeResponse | null
  fitAnalysis: ResumeFitAnalysisResponse | null
  onEdit: () => void
  onViewDetails?: () => void
  forceExpanded?: boolean
  showChangeButton?: boolean
  showViewDetailsButton?: boolean
}

export const AssignedResumeViewer: React.FC<AssignedResumeViewerProps> = ({
  jobApplicationId,
  selectedResumeId,
  resumeSelectionType,
  availableResumes,
  tailoredResume,
  fitAnalysis,
  onEdit,
  onViewDetails,
  forceExpanded = false,
  showChangeButton = true,
  showViewDetailsButton = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false)
  const [isDownloadingDocx, setIsDownloadingDocx] = useState(false)

  // Determine which resume is assigned
  const assignedResume = useMemo(() => {
    if (!resumeSelectionType) return null
    if (resumeSelectionType === 'custom' && selectedResumeId?.startsWith('tailored-')) {
      return availableResumes.find(r => r.id === selectedResumeId)
    }
    if (resumeSelectionType === 'custom' && tailoredResume) {
      return availableResumes.find(r => r.id === tailoredResume.baseResumeId)
    }
    if (resumeSelectionType === 'saved' && selectedResumeId) {
      return availableResumes.find(r => r.id === selectedResumeId)
    }
    return null
  }, [resumeSelectionType, selectedResumeId, tailoredResume, availableResumes])

  // Get fit score for assigned resume
  const fitScore = useMemo(() => {
    if (!fitAnalysis || !assignedResume) return null
    const analysis = fitAnalysis.resumeFitAnalyses?.find(a => a.resumeId === assignedResume.id)
    return analysis?.fitScore || null
  }, [fitAnalysis, assignedResume])

  const canExport =
    resumeSelectionType === 'custom' &&
    !!(tailoredResume?.resumeContent || tailoredResume?.resumeText)

  const handleDownloadPdf = useCallback(async () => {
    if (!canExport) return
    setIsDownloadingPdf(true)
    try {
      await downloadTailoredResumeArtifact(jobApplicationId, 'pdf')
      toast.success('PDF download is ready')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to generate PDF')
    } finally {
      setIsDownloadingPdf(false)
    }
  }, [canExport, jobApplicationId])

  const handleDownloadDocx = useCallback(async () => {
    if (!canExport) return
    setIsDownloadingDocx(true)
    try {
      await downloadTailoredResumeArtifact(jobApplicationId, 'docx')
      toast.success('DOCX download is ready')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to generate DOCX')
    } finally {
      setIsDownloadingDocx(false)
    }
  }, [canExport, jobApplicationId])

  const isExpandedView = forceExpanded || isExpanded

  // Early return AFTER all hooks (Rules of Hooks requirement)
  if (!resumeSelectionType) {
    return null
  }

  const getFitScoreColor = (score: number): string => {
    if (score >= 80) return 'excellent'
    if (score >= 60) return 'good'
    if (score >= 40) return 'moderate'
    return 'low'
  }

  const getFitScoreLabel = (score: number): string => {
    if (score >= 80) return 'Excellent Match'
    if (score >= 60) return 'Good Match'
    if (score >= 40) return 'Moderate Match'
    return 'Low Match'
  }

  return (
    <div className="assigned-resume-viewer">
      <div className="assigned-resume-viewer-header">
        <div className="assigned-resume-viewer-title">
          <h3>📄 Assigned Resume</h3>
          {resumeSelectionType === 'custom' && (
            <span className="assigned-resume-type-badge custom">Custom Tailored</span>
          )}
          {resumeSelectionType === 'saved' && (
            <span className="assigned-resume-type-badge saved">Saved Resume</span>
          )}
        </div>
        <div className="assigned-resume-viewer-actions">
          {canExport && (
            <>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleDownloadPdf}
                disabled={isDownloadingPdf}
              >
                {isDownloadingPdf ? 'Preparing PDF...' : 'Download PDF'}
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleDownloadDocx}
                disabled={isDownloadingDocx}
              >
                {isDownloadingDocx ? 'Preparing DOCX...' : 'Download DOCX'}
              </button>
            </>
          )}
          {/* Only show View Details if there's actual content to show */}
          {showViewDetailsButton && (resumeSelectionType !== 'custom' || (tailoredResume && tailoredResume.resumeContent)) && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                if (onViewDetails) {
                  onViewDetails()
                } else {
                  setIsExpanded(!isExpanded)
                }
              }}
              title={forceExpanded ? 'Viewing details' : isExpandedView ? 'Hide Details' : 'View Details'}
              disabled={forceExpanded}
            >
              {forceExpanded ? 'Viewing Details' : isExpandedView ? 'Hide Details' : 'View Details'}
            </button>
          )}
          {showChangeButton && (
            <button
              className="btn btn-primary btn-sm"
              onClick={onEdit}
            >
              Change Resume
            </button>
          )}
        </div>
      </div>

      {assignedResume ? (
        <div className="assigned-resume-viewer-content">
          <div className="assigned-resume-viewer-main">
            <div className="assigned-resume-viewer-info">
              <p className="assigned-resume-name">
                {assignedResume.originalFilename || assignedResume.filename || 'Resume'}
              </p>
              {fitScore !== null && (
                <div className={`assigned-resume-fit-score ${getFitScoreColor(fitScore)}`}>
                  <span className="assigned-resume-fit-score-value">{Math.round(fitScore)}%</span>
                  <span className="assigned-resume-fit-score-label">{getFitScoreLabel(fitScore)}</span>
                </div>
              )}
            </div>
          </div>

          {isExpandedView && (
            <div className="assigned-resume-viewer-details">
              {resumeSelectionType === 'custom' && tailoredResume && (
                <div className="assigned-resume-custom-info">
                  <h4>Custom Tailored Resume</h4>
                  
                  {/* Show resume content if available */}
                  {tailoredResume.resumeContent && (
                    <div className="assigned-resume-content">
                      <h5>Generated Resume Content</h5>
                      <div className="assigned-resume-content-text">
                        <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word', fontFamily: 'inherit' }}>
                          {tailoredResume.resumeContent}
                        </pre>
                      </div>
                    </div>
                  )}
                  
                  {/* Show resume text if content is not available but text is */}
                  {!tailoredResume.resumeContent && tailoredResume.resumeText && (
                    <div className="assigned-resume-content">
                      <h5>Generated Resume Text</h5>
                      <div className="assigned-resume-content-text">
                        <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word', fontFamily: 'inherit' }}>
                          {tailoredResume.resumeText}
                        </pre>
                      </div>
                    </div>
                  )}
                  
                  {/* Show message if no content available */}
                  {!tailoredResume.resumeContent && !tailoredResume.resumeText && (
                    <div className="assigned-resume-no-content">
                      <p>No resume content available. The tailored resume may not have been generated yet.</p>
                      {tailoredResume.status === 'wizard_in_progress' && (
                        <p className="assigned-resume-status-note">Status: Generation in progress...</p>
                      )}
                      {tailoredResume.status === 'not_started' && (
                        <p className="assigned-resume-status-note">Status: Not started. Please generate a tailored resume first.</p>
                      )}
                    </div>
                  )}
                  
                  {/* Metadata */}
                  <div className="assigned-resume-metadata">
                    {canExport && (
                      <div className="assigned-resume-export-note">
                        <strong>Export tip:</strong> Use the Download PDF or Download DOCX actions for submissions. Both exports preserve real text and use the latest stored tailored resume content.
                      </div>
                    )}
                    {tailoredResume.wizardPreferences && (
                      <div className="assigned-resume-info-item">
                        <span className="assigned-resume-info-label">Generated with:</span>
                        <span className="assigned-resume-info-value">
                          {tailoredResume.wizardPreferences.tones
                            ? tailoredResume.wizardPreferences.tones
                                .map(t => t.charAt(0).toUpperCase() + t.slice(1))
                                .join(' + ') + ' tone'
                            : 'Default settings'}
                          {tailoredResume.wizardPreferences.length && `, ${tailoredResume.wizardPreferences.length} length`}
                        </span>
                      </div>
                    )}
                    <div className="assigned-resume-info-item">
                      <span className="assigned-resume-info-label">Status:</span>
                      <span className={`assigned-resume-info-value status-${tailoredResume.status}`}>
                        {tailoredResume.status === 'generated' && 'Generated'}
                        {tailoredResume.status === 'editing' && 'Editing'}
                        {tailoredResume.status === 'wizard_in_progress' && 'Wizard In Progress'}
                        {tailoredResume.status === 'not_started' && 'Not Started'}
                      </span>
                    </div>
                    {tailoredResume.updatedAt && (
                      <div className="assigned-resume-info-item">
                        <span className="assigned-resume-info-label">Last Updated:</span>
                        <span className="assigned-resume-info-value">
                          {new Date(tailoredResume.updatedAt).toLocaleString()}
                        </span>
                      </div>
                    )}
                    {tailoredResume.createdAt && (
                      <div className="assigned-resume-info-item">
                        <span className="assigned-resume-info-label">Created:</span>
                        <span className="assigned-resume-info-value">
                          {new Date(tailoredResume.createdAt).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {fitAnalysis && assignedResume && (
                <div className="assigned-resume-fit-details">
                  <h4>Fit Analysis</h4>
                  {(() => {
                    const analysis = fitAnalysis.resumeFitAnalyses?.find(a => a.resumeId === assignedResume.id)
                    if (!analysis) return null
                    return (
                      <div className="assigned-resume-fit-metrics">
                        <div className="assigned-resume-fit-metric">
                          <span className="assigned-resume-fit-metric-label">Skills Match:</span>
                          <span className="assigned-resume-fit-metric-value">{Math.round(analysis.skillsMatch)}%</span>
                        </div>
                        <div className="assigned-resume-fit-metric">
                          <span className="assigned-resume-fit-metric-label">Experience Match:</span>
                          <span className="assigned-resume-fit-metric-value">{Math.round(analysis.experienceMatch)}%</span>
                        </div>
                        <div className="assigned-resume-fit-metric">
                          <span className="assigned-resume-fit-metric-label">Education Match:</span>
                          <span className="assigned-resume-fit-metric-value">{Math.round(analysis.educationMatch)}%</span>
                        </div>
                      </div>
                    )
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="assigned-resume-viewer-empty">
          <p>No resume assigned. Click "Change Resume" to select one.</p>
        </div>
      )}
    </div>
  )
}
